import { useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useCreateSamlUserMutation } from "../api/user/useCreateSamlUserMutation"
import { FORM_ACTIONS } from "../utils/formActions"
import { useFormData } from "../utils/formReducer"
import { useAuth } from "./useAuth"
import { useReferrer } from "./useReferrer"

/**
 * When a SAML assertion from Auth0 redirects to signup, the redirect includes an
 * opaque access_token. The opaque access_token indicates that the user has been
 * authorized by auth0.
 *
 * If the conditions are right (utm_source is a SAML affiliate and access_token exists),
 * the startOnboarding function will POST to the API which should create the user in
 * our database and return enough user data to prefill registration.
 *
 * If the utm_source is not a SAML affiliate or there is no access_token (userToken),
 * the POST to the API will not happen and the flow will proceed as usual.
 */
export const useStartOnboarding = () => {
  const [error, setError] = useState<string | null>(null)
  const { userToken} = useAuth()
  const { dispatch} = useFormData()
  const { mutation: createSamlUserMutation} = useCreateSamlUserMutation()
  const [searchParams] = useSearchParams()
  const isSamlConnection = !!useReferrer()?.is_saml

  const prefillRegistrationFormField = (field: string, value?: string) => {
    if (value) {
      dispatch({
        type: FORM_ACTIONS.UPDATE_FORM_FIELD,
        fieldName: field,
        payload: value,
      });
    }
  };

  const startOnboarding = async (handleStartFlow: () => void) => {
    const utmSource = searchParams.get('utm_source')

    if (userToken && isSamlConnection && utmSource) {
      try {
        const { first_name, last_name, email } =
          await createSamlUserMutation.mutateAsync({ referrer_utm_source: utmSource });

        prefillRegistrationFormField('firstName', first_name);
        prefillRegistrationFormField('lastName', last_name);
        prefillRegistrationFormField('email', email);

        setError(null)
        handleStartFlow()
      } catch (error:any) {
        console.error(error.message);
        setError(error.message)
      }
    } else {
      setError(null)
      handleStartFlow()
    }
  }

  return {
    startOnboarding,
    error,
  }
}